import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, FileSpreadsheet, Download } from 'lucide-react';
import { api, errMsg, API_URL, tokenStore } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const TEMPLATE = 'name,type,company,job_title,email,phone,mobile,website,street,street2,city,state,zip,country,tax_id,birthday,tags,is_customer,is_vendor,notes\nPT Maju Jaya,company,,,info@majujaya.id,0211234567,,https://majujaya.id,Jl. Sudirman 1,,Jakarta,DKI Jakarta,10220,Indonesia,01.234.567.8-901.000,,VIP;Distributor,1,1,\nBudi Santoso,person,PT Maju Jaya,Purchasing,budi@majujaya.id,,081234567890,,,,Jakarta,,,Indonesia,,1990-05-17,VIP,1,0,Kontak utama\n';

export const downloadExport = async (companyId) => {
  const res = await fetch(`${API_URL}/companies/${companyId}/contacts/export`, { headers: { Authorization: `Bearer ${tokenStore.access}` } });
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'kontak.csv';
  a.click();
  URL.revokeObjectURL(a.href);
};

export const ImportDialog = ({ companyId, open, onClose, onDone, lang }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const id = lang === 'id';
  const run = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/companies/${companyId}/contacts/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success(id ? `${data.created} kontak diimpor` : `${data.created} contacts imported`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = () => { setFile(null); setResult(null); onClose(); };
  const template = () => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv' })); a.download = 'template-kontak.csv'; a.click(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md" data-testid="contact-import-dialog">
        <DialogHeader><DialogTitle className="font-display">{id ? 'Impor Kontak (CSV)' : 'Import Contacts (CSV)'}</DialogTitle><DialogDescription>{id ? 'Kolom: name, type (person/company), company, email, phone, birthday (YYYY-MM-DD), tags (pisahkan dengan ;), dll.' : 'Columns: name, type (person/company), company, email, phone, birthday (YYYY-MM-DD), tags (separated by ;), etc.'}</DialogDescription></DialogHeader>
        <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border p-8 text-sm text-muted-foreground hover:bg-muted/50" data-testid="contact-import-dropzone">
          <FileSpreadsheet size={28} className="text-primary" />
          {file ? <span className="font-medium text-foreground">{file.name}</span> : <span>{id ? 'Klik untuk memilih file CSV' : 'Click to choose a CSV file'}</span>}
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="contact-import-file" />
        <button type="button" onClick={template} className="text-xs text-primary underline-offset-2 hover:underline text-left inline-flex items-center gap-1"><Download size={12} />{id ? 'Unduh template CSV' : 'Download CSV template'}</button>
        {result && (
          <div className="rounded-xl bg-muted/60 p-3 text-sm" data-testid="contact-import-result">
            <div>{id ? 'Berhasil' : 'Created'}: <b>{result.created}</b> · {id ? 'Error' : 'Errors'}: <b>{result.error_count}</b></div>
            {result.errors?.length > 0 && <ul className="mt-1 max-h-24 overflow-y-auto text-xs text-rose-700 list-disc pl-4">{result.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-xl" onClick={close}>{id ? 'Tutup' : 'Close'}</Button>
          <Button type="button" className="rounded-xl" disabled={!file || busy} onClick={run} data-testid="contact-import-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={14} className="mr-1.5" />{id ? 'Impor' : 'Import'}</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
